import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ButtonCst from "@components/ButtonCst";
import CloseIcon from "@mui/icons-material/Close";
import UploaderDropZoneCst from "../UploaderDropZoneCst";
import { useAuth } from "@context/auth";
import TooltipInput from "@components/InputCst/TooltipInput";
import { AxiosResponse } from "axios";

const DownloadUploadModalCst: React.FC<{
  data: any;
  title: string;
  onCancelAction: () => void;
  onCompleteDisclaimer?: boolean;
  infoMessage?: string;
  onDownloadingChange?: (isDownloading: boolean) => void;
}> = ({
  data,
  title,
  onCancelAction,
  onCompleteDisclaimer,
  infoMessage,
  onDownloadingChange,
}) => {
  // const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const { t } = useTranslation(["upload-component"]);
  const [errorResponse, setErrorResponse] = useState([]);
  const [apiResponse, setApiResponse] = useState<AxiosResponse | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDownload =
    data.types.findIndex(
      (el: string) => el.includes("download") || el.includes("template"),
    ) >= 0;
  const { user } = useAuth();

  let canUpload: boolean = true;
  if (data.permissionUpload) {
    canUpload = false;
    if (
      user?.permissions.find(
        (permission) => permission === data.permissionUpload,
      )
    ) {
      canUpload = true;
    }
  }

  const hasDisableProp = () => {
    if (typeof data.downloadDisabled === "boolean") {
      return !data.downloadDisabled;
    } else {
      return false;
    }
  };

  const handleDownload = async (type?: string) => {
    setIsDownloading(true);
    onDownloadingChange?.(true);
    try {
      await data.onDownload(type);
    } finally {
      setIsDownloading(false);
      onDownloadingChange?.(false);
    }
  };

  const renderDownloadButton = React.useMemo(() => {
    return data.types
      .filter(
        (type: string) =>
          type.includes("download") || type.includes("template"),
      )
      .map((el: string) => (
        <ButtonCst
          key={el}
          sx={{
            width: "100%",
          }}
          id="add-file"
          variant={el === "template" ? "contained" : "outlined"}
          onClick={() => handleDownload(el === "template" ? el : undefined)}
          disabled={
            el === "template"
              ? isDownloading
              : hasDisableProp() || isDownloading
          }
        >
          {isDownloading && el !== "template" ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <DownloadIcon />
          )}{" "}
          {el === "template"
            ? t("download-template")
            : isDownloading
              ? t("downloading")
              : t("download")}
        </ButtonCst>
      ));
  }, [data, t, isDownloading]);

  const { tooltipFile, tooltipFileIta, tooltipFileEn } = data;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            maxWidth: "80%",
          }}
        >
          <h2 style={{ margin: "0" }} id="parent-modal-title">
            {title}
          </h2>
          {tooltipFile && (
            <TooltipInput
              hrefInput
              hrefIta={tooltipFileIta}
              hrefEn={tooltipFileEn}
            />
          )}
        </Box>
        <Button
          onClick={() => onCancelAction()}
          disabled={isDownloading}
          sx={{
            borderRadius: "50%",
            minWidth: "0",
            padding: "0 3px",
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            span: {
              width: "0",
            },
          }}
        >
          <CloseIcon fontSize="large" />
        </Button>
      </Box>
      {data.disclaimer && (
        <Typography
          sx={{
            mt: 2,
            fontStyle: "italic",
            color: "text.primary",
          }}
        >
          {data.disclaimer}
        </Typography>
      )}

      <Stack
        direction={"column"}
        alignItems={"start"}
        sx={{
          minHeight: "300px",
          paddingTop: "30px",
        }}
      >
        {infoMessage && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ mb: 2, textAlign: "left", marginBottom: "30px" }}
          >
            {infoMessage}
          </Alert>
        )}

        <Grid container spacing={4}>
          {isDownload && (
            <Grid
              xs={12}
              lg={6}
              item
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              {renderDownloadButton}
            </Grid>
          )}

          {canUpload && (
            <Grid
              xs={12}
              lg={6}
              key={data.types}
              item
              sx={{ textAlign: "center" }}
            >
              <UploaderDropZoneCst
                accept={data.accept}
                data={data}
                callBack={(File: FormData) => {
                  setApiResponse(null);
                  setUploadComplete(false);
                  setErrorResponse([]);
                  return data.onUpload(File);
                }}
                errorResponse={(error: any) => setErrorResponse(error)}
                setApiResponse={setApiResponse}
                onUploadComplete={(success: boolean) =>
                  setUploadComplete(success)
                }
              />
            </Grid>
          )}
        </Grid>
      </Stack>

      {apiResponse &&
        apiResponse?.headers["content-disposition"] &&
        apiResponse?.headers["content-disposition"].includes("filename") && (
          <Box
            sx={{
              maxHeight: "200px",
              overflow: "auto",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography>{`${t("OKwithErrors")}`}</Typography>
            <ButtonCst
              id={"download-csv"}
              variant={"outlined"}
              icon={"download"}
              size={"medium"}
              sx={{
                minWidth: "50px",
                mr: "auto",
                marginLeft: "20px",
              }}
              onClick={() => {
                const type = apiResponse.headers["content-type"];
                const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
                const blob = new Blob([bom, apiResponse.data], { type: type });
                let downloadUrl = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.setAttribute("target", "_blank");
                document.body.appendChild(link);
                link.href = downloadUrl;
                link.download =
                  apiResponse?.headers["content-disposition"].split(
                    "filename=",
                  )[1];
                link.click();
                document.body.removeChild(link);
              }}
            />
          </Box>
        )}
      {uploadComplete && onCompleteDisclaimer && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: "rgba(46, 125, 50, 0.1)",
            border: "1px solid",
            borderColor: "success.main",
          }}
        >
          <Typography
            sx={{
              color: "success.main",
              fontSize: "0.875rem",
            }}
          >
            {t("on-complete-disclaimer")}
          </Typography>
        </Box>
      )}
      {errorResponse && (
        <Box sx={{ maxHeight: "200px", overflow: "auto" }}>
          {errorResponse &&
            errorResponse.map((item: any, index) => (
              <Typography key={index}>
                {item?.message ? item?.message : item}
              </Typography>
            ))}
        </Box>
      )}
    </>
  );
};

export default DownloadUploadModalCst;
